// Tipurile Pasului / zonei «pas14» — desprinse din `tipuri.ts`; orice importator
// le ia tot din `./tipuri` (bariera re-exportă fiecare fișier de aici).

import type { VerificareArmare } from "./pas13";
import type { SubsolRigid } from "./pas7";

// --- Pasul 14 — armarea pereților de subsol (cutia rigidă) ------------------

// Regimul secțiunii: grindă zveltă (l₀ ≥ 3·h, SR EN 1992-1-1 §5.3.1(3)) sau
// grindă-perete — la a doua, CR 2-1-1.1 §10.3.2 cere mecanismul de grindă cu
// zăbrele, deci armătura întinsă se concentrează în banda de la fața întinsă.
export interface RegimPerete {
  l0_h: number;
  L_ax_h?: number | null;
  prag: number;
  grinda_perete: boolean;
  z_bernoulli_m: number;
  z_grinda_perete_m: number;
  banda_tirant_cm: number;
  banda_tirant_frac: number;
  referinta: string;
  nota_convenție?: string;
}

export interface RostPerete {
  v_edi_kpa: number;
  v_rdi_kpa: number;
  v_rdi_brut_kpa: number;
  plafon_kpa: number;
  c: number;
  mu: number;
  rho: number;
  sigma_n_kpa: number;
  dinamic: boolean;
  termen_beton_kpa: number;
  termen_otel_kpa: number;
  punct?: string;
  grupa?: string;
  combinatie?: string;
  z_m?: number;
  beta?: number;
}

export interface NodReazemPerete {
  theta_grade: number;
  F_biela_kn: number;
  aria_m2: number;
  sigma_kpa: number;
  sigma_rd_kpa: number;
}

export interface FisuraPerete {
  punct: string;
  M_knm: number;
  wk_mm: number;
  As_cm2?: number;
  sigma_s?: number;
  s_rmax_mm?: number;
  z_cm?: number;
  rho_eff?: number;
  verdict: string;
}

export interface AncorarePerete {
  f_bd_mpa: number;
  l_b_rqd_cm: number;
  l_bd_cm: number;
  l_0_cm: number;
  alfa_6: number;
}

export interface TirantPerete {
  n: number;
  phi_mm: number;
  As_cm2: number;
}

export interface ArmarePerete {
  element: string;
  nivel: string;
  stalp_i?: string | null;
  stalp_j?: string | null;
  L_ax_m: number;
  l0_m?: number;
  off_i_m?: number | null;
  off_j_m?: number | null;
  t_cm: number;
  h_cm: number;
  perimetral?: boolean;
  e_fata_m?: number | null;
  beton_m3: number;
  otel_kg: number;
  verdict: string;
  nr_incercari?: number;
  regim: RegimPerete;
  armare: {
    vertical: { phi_mm: number; pas_mm: number; rho_pct: number; nr_bare?: number };
    orizontal: {
      phi_mm: number;
      pas_mm: number;
      rho_pct: number;
      nr_bare?: number;
      nr_randuri?: number;
      nr_randuri_banda?: number;
    };
    tirant_sus: TirantPerete;
    tirant_jos: TirantPerete;
    agrafe: { phi_mm: number; per_m2: number; nr: number; carlig?: string };
    As_plasa_banda_cm2?: number;
    d1_cm?: number;
  };
  puncte: {
    punct: string;
    grupa: string;
    combinatie: string;
    loc_m: number;
    M3_knm: number;
    V2_kn: number;
    P_kn: number;
    amplificat?: boolean;
    M3_gs_knm?: number;
    V2_gs_kn?: number;
    P_gs_kn?: number;
  }[];
  verificari: VerificareArmare[];
  rost?: RostPerete | null;
  nod_reazem?: NodReazemPerete | null;
  v_rd_c_kn?: number;
  sls?: {
    fisuri: FisuraPerete[];
    w_max_mm: number;
    M_cr_knm: number;
    E_eff_mpa: number;
    phi_fluaj: number;
    nota?: string;
  } | null;
  ancorare?: AncorarePerete | null;
  note?: string[];
}

// Pasul 14 — o lamelă (element vertical cu latura lungă ≥ 4·b) pe un nivel,
// armată ca perete structural (`armare_lamele.py`): carcasele de capăt,
// plasele inimii, etrierii capetelor și verificările ei
export interface ArmareLamela {
  element: string;             // id-ul de plan al nivelului, „ST12.3"
  stack: string;
  nivel: string;
  etaj: number;
  t_cm: number;
  L_cm: number;
  L_ax_m: number;
  h_cm: number;
  H_m: number;
  h_s_m: number;
  sectiune_compusa?: boolean;
  zona: "A" | "B" | "infrastructura";
  la_baza?: boolean;
  primul_nivel?: boolean;
  plantat?: boolean;
  armare: {
    capete: {
      n: number;
      phi_mm: number;
      nr_capete: number;
      l_cm: number;
      rho_pct: number;
      /** marca de bară scrisă de pas (`tipuri_armare`); lipsește pe rulările
       *  de dinaintea registrului */
      tip_armare?: string;
      tip_armare_mustati?: string;
      etrieri: {
        phi_mm: number;
        pas_cm: number;
        pas_max_cm?: number;
        l_confinat_cm: number;
        ochi_cm: [number, number];
        nr_pe_capat: number;
        nr_ramuri_grosime?: number;
        /** agrafele de confinare ale unui set de etrieri: câte una pe rând de
         *  bare cuprins, fără cele două ramuri ale etrierului perimetral */
        agrafe_pe_set?: number;
        /** componentele lungimii confinate în zona critică (P100-1
         *  §5.3.4.3.2(6)): x_u·(1 − ε_cu2/ε_cu2,c) și max(0,15·l_w ; 1,5·b_w);
         *  null în afara zonei A */
        l_x_cm?: number | null;
        l_c_min_cm?: number | null;
        omega_wd?: number;
        alpha?: number;
        tip_armare?: string;
        tip_armare_agrafe?: string;
      };
    };
    vertical: { phi_mm: number; pas_mm: number; nr_bare: number; rho_pct: number;
                tip_armare?: string; tip_armare_mustati?: string };
    orizontal: { phi_mm: number; pas_mm: number; nr_bare: number; rho_pct: number;
                 tip_armare?: string };
    agrafe: { phi_mm: number; per_m2: number; nr: number; tip_armare?: string };
  };
  eforturi?: {
    omega?: number | null;
    epsilon?: number;
    M_Rd0_knm?: number | null;
    M_Ed0_knm?: number | null;
    V_Ed_kn?: number;
    x_u_cm?: number | null;
    x_u_lim_cm?: number | null;
    nu_d?: number | null;
  };
  verificari: (VerificareArmare & { detaliu?: Record<string, unknown>; unitate?: string })[];
  verdict: string;
  re_min?: number | null;
  beton_m3: number;
  otel_kg: number;
  /** «ceapa» P–M–M a secțiunii adoptate, în axele cepei stâlpilor
   *  (`armare_lamele._ceapa`): θ → [[N, M]], M_tare pe θ = 0; și punctele
   *  verificate pe ea, [N, M_slab, M_tare]. Lipsesc pe rulările vechi. */
  suprafata?: Record<string, number[][]>;
  puncte?: number[][];
}

// Pasul 14 — o poziție de lamelă, cu ce vine de la baza ei
export interface StivaLamela {
  stack: string;
  baza: string | null;
  l_w_m: number;
  b_cm: number;
  H_w_m: number;
  n_niveluri: number;
  l_cr_m: number;
  scurt: boolean;
  omega: number | null;
  epsilon: number;
  M_Rd0_knm: number;
  M_Ed0_knm: number;
  V_Ed0_kn: number;
  zona_A: string[];
  infrastructura: string[];
  verdict: string;
}

export interface ArmarePeretiResult {
  activ: boolean;
  // lamelele, armate ca pereți structurali — independente de `activ`, care e
  // al panourilor de subsol
  lamele?: ArmareLamela[];
  nr_lamele?: number;
  stive_lamele?: StivaLamela[];
  verdicte_lamele?: Record<string, number>;
  otel_lamele_kg?: number;
  beton_lamele_m3?: number;
  nu_se_aplica_lamele?: {
    nume: string;
    la_stalpi: string;
    motiv: string;
    referinta: string;
  }[];
  parametri_lamele?: {
    clasa_ductilitate?: string;
    /** acoperirea de beton a lamelelor [cm] */
    c_cm?: number;
    sistem_calcul?: string;
    q?: number;
    k_M?: number;
    k_V?: number;
    gamma_rd?: number;
    gamma_rd_sursa?: string;
    xi_u?: number | null;
    raport_lamela?: number;
    // T₁ pe direcții, din Pasul 9; null ⇒ rezerva de 0,5 s
    T1_s?: Record<string, number> | null;
    T1_rezerva_s?: number | null;
    limite?: string[];
  };
  // catalogul de carcase al cofrajelor de lamelă (b × l_w × zonă)
  carcase_lamele?: {
    cofraje: {
      cofraj: string;
      nr: number;
      din: number;
      elemente: string[];
      capete: string;
      inima: string;
      etrieri: string;
      nota?: string;
    }[];
    surplus_otel_pct: number;
    prag_pct: number;
    nr_carcase_catalog: number;
    nr_carcase_distincte: number;
    nr_recalculate: number;
    pozitii_refuzate: string[];
  };
  avertismente_lamele?: string[];
  // clădirea CERE subsol rigid, dar n-a ieșit niciun panou (activ = false
  // cu cerut = true e o problemă de geometrie, nu un pas inaplicabil)
  cerut?: boolean;
  motiv?: string;
  subsol_rigid?: SubsolRigid | null;
  pereti: ArmarePerete[];
  nr_pereti: number;
  nr_grinzi_perete?: number;
  verdicte: Record<string, number>;
  otel_total_kg?: number;
  beton_total_m3?: number;
  avertismente?: string[];
  // verificările de la grinzi care NU se preiau, fiecare cu articolul care
  // scutește — întrebarea „se aplică și X?" are nevoie de răspuns scris
  nu_se_aplica?: {
    nume: string;
    la_grinzi: string;
    motiv: string;
    referinta: string;
  }[];
  recomandari_cutie_rigida?: string[];
  parametri?: {
    beton?: string;
    otel?: string;
    f_cd_mpa?: number;
    f_ctd_mpa?: number;
    f_ctm_mpa?: number;
    f_yd_mpa?: number;
    acoperire_cm?: number;
    amplificare_infrastructura?: number;
    rho_min_pct?: number;
    acceptare_grinzi_pct?: number;
    prag_re_acceptare?: number;
    fisura_wmax_mm?: number;
    raport_grinda?: number;
    banda_tirant_frac?: number;
    rost?: { c: number; mu: number; nota?: string };
    referinte?: string[];
    nota_editie_cr2?: string;
  };
  error?: string;
}

export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
